/**
 * FAQs — the target of the footer's "FAQs" link.
 *
 * Every answer here restates something the site already commits to elsewhere: the shipping
 * and GST figures resolve from DEFAULT_PRICING_CONFIG (what checkout actually charges), the
 * payment methods are the three offered in checkout.tsx, and the material, size and care
 * answers match src/config/collections.ts. Nothing on this page is a new claim, which is the
 * only reason it could be written without the owner supplying copy.
 *
 * No FAQPage JSON-LD, on purpose, for two independent reasons. The page is noindex, and
 * Google only reads FAQ markup on an indexable page -- but more importantly the four
 * /collections/<slug> pages already emit FAQPage schema via getFaqSchema(), and adding a
 * fifth source repeating the same pot and care answers would hand crawlers competing copies
 * of the same Q&A. If this page is ever published, decide which surface owns FAQ markup
 * before adding it here.
 *
 * TODO (owner): questions this cannot answer without a business fact -- international
 * shipping, bulk and trade orders, whether custom sizes or arrangements are made to order,
 * and GST invoices for business buyers. See also the TODO in shipping.tsx.
 */

import { createFileRoute, Link } from "@tanstack/react-router";
import { Reveal } from "@/components/common/reveal";
import { siteConfig } from "@/config/site";
import { formatPrice } from "@/components/forms/price-display";
import { DEFAULT_PRICING_CONFIG } from "@/lib/pricing-calculator";
import { useStoreShippingSettings } from "@/lib/store-settings";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: `Frequently Asked Questions — ${siteConfig.name}` },
      {
        name: "description",
        content:
          "Answers on materials, sizes, cleaning, delivery charges, GST, payment methods and returns for Aster Decorz artificial plants.",
      },
      { property: "og:title", content: `Frequently Asked Questions — ${siteConfig.name}` },
      {
        property: "og:description",
        content:
          "Answers on materials, sizes, cleaning, delivery charges, GST, payment methods and returns for Aster Decorz artificial plants.",
      },
      { property: "og:url", content: `${siteConfig.url}/faq` },
    ],
    links: [{ rel: "canonical", href: `${siteConfig.url}/faq` }],
  }),
  component: Faq,
});

function Faq() {
  const shippingSettings = useStoreShippingSettings();
  const { gstRate } = DEFAULT_PRICING_CONFIG;
  const standardShippingFee = shippingSettings.standardShippingFee;
  const expressShippingFee = shippingSettings.expressShippingFee;
  const freeShippingThreshold = shippingSettings.freeShippingThreshold;
  const standardDays = shippingSettings.standardDays;
  const { returnDays } = siteConfig.shipping;

  const groups: { title: string; items: { q: string; a: React.ReactNode }[] }[] = [
    {
      title: "The pieces",
      items: [
        {
          q: "Are these real plants?",
          a: (
            <>
              No — everything in the catalogue is artificial, and that is the point. Pieces are
              built from hand-dyed silk, matte polymer veining and UV-stable pigment on stoneware or
              fluted ceramic pots. They give the texture and presence of botanicals with zero
              watering, zero light requirements and zero pruning.
            </>
          ),
        },
        {
          q: "Does every piece come with a pot?",
          a: (
            <>
              Yes. Every item ships already potted and already arranged — nothing to trim, no vase
              to find. The specific planter varies by piece, from a white fluted ceramic to a footed
              bonsai pot, and is named on each product page as part of what ships.
            </>
          ),
        },
        {
          q: "How tall are they?",
          a: (
            <>
              Between 18 cm and 29 cm across the catalogue, with the exact height and canopy width
              listed on every product page. These are tabletop, shelf and console pieces rather than
              floor-standing trees — worth checking against your space before ordering.
            </>
          ),
        },
        {
          q: "How do I clean them?",
          a: (
            <>
              Wipe the foliage with a soft dry cloth, or dust with a soft brush twice a year. No
              water, no spray and no polish. Dusting is the whole of the upkeep.
            </>
          ),
        },
        {
          q: "Can I put them outdoors?",
          a: (
            <>
              They are finished exclusively for indoor spaces. Sustained direct harsh sun will fade
              any artificial foliage over time, so a shaded or covered balcony is recommended over
              an open terrace.
            </>
          ),
        },
        {
          q: "Will they look right in a dark room or a windowless office?",
          a: (
            <>
              Yes — light is irrelevant to artificial foliage, which is the main reason to choose
              it. Windowless bathrooms, interior corners, meeting rooms and desks under artificial
              light all work equally well.
            </>
          ),
        },
      ],
    },
    {
      title: "Ordering & delivery",
      items: [
        {
          q: "What does delivery cost?",
          a: (
            <>
              {formatPrice(standardShippingFee)} for standard delivery ({standardDays} working days)
              and {formatPrice(expressShippingFee)} for express. Standard delivery is free on orders
              over {formatPrice(freeShippingThreshold)}; express is charged whatever the order
              value. Full detail on the{" "}
              <Link to="/shipping" className="text-primary underline underline-offset-4">
                shipping page
              </Link>
              .
            </>
          ),
        },
        {
          q: "Is GST included in the price shown?",
          a: (
            <>
              Yes. All product prices displayed on the site are inclusive of{" "}
              {Math.round(gstRate * 100)}% GST (MRP). There are no surprise tax markups added at
              checkout — the selling price already covers statutory taxes.
            </>
          ),
        },
        {
          q: "How can I pay?",
          a: (
            <>
              UPI (PhonePe, Google Pay, Paytm), credit and debit cards, and net banking — all
              handled by Razorpay. Card details are entered on Razorpay's own secure form and never
              reach this site. Payment is online only; there is no cash-on-delivery option.
            </>
          ),
        },
        {
          q: "Can I return something?",
          a: (
            <>
              Yes, within {returnDays} days of delivery, in the original crate and packaging. Email{" "}
              <a
                href={`mailto:${siteConfig.contact.email}?subject=Return%20request`}
                className="text-primary underline underline-offset-4"
              >
                {siteConfig.contact.email}
              </a>{" "}
              with your order number and photos to start a return. Once received and inspected,
              refunds are credited within 5–7 business days to your original payment method.
            </>
          ),
        },
        {
          q: "Can I cancel an order?",
          a: (
            <>
              No. We do not accept cancellations once an order is placed and confirmed, as orders
              are immediately sent to our warehouse for packaging and prompt dispatch.
            </>
          ),
        },
        {
          q: "Where is my order?",
          a: (
            <>
              Order status sits in{" "}
              <Link to="/account/orders" className="text-primary underline underline-offset-4">
                your orders
              </Link>{" "}
              once you are signed in. If something looks wrong there, email us with the order
              number.
            </>
          ),
        },
      ],
    },
  ];

  return (
    <div className="pb-28 pt-24 md:pt-28">
      <div className="section-container">
        <Reveal>
          <p className="eyebrow">Support</p>
          <h1 className="mt-6 max-w-3xl">Frequently asked questions</h1>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Materials, sizes, upkeep, delivery and returns. If your question is not here, email{" "}
            <a
              href={`mailto:${siteConfig.contact.email}`}
              className="text-primary underline underline-offset-4"
            >
              {siteConfig.contact.email}
            </a>
            .
          </p>
        </Reveal>

        <div className="mt-16 max-w-3xl space-y-14">
          {groups.map((group, gi) => (
            <Reveal key={group.title} delay={gi * 80}>
              <h2 className="font-display text-2xl">{group.title}</h2>
              <dl className="mt-6 divide-y border-t">
                {group.items.map((item) => (
                  <div key={item.q} className="py-5">
                    <dt className="text-sm font-medium">{item.q}</dt>
                    <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.a}</dd>
                  </div>
                ))}
              </dl>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
